import { t, Trans } from '@lingui/macro';
import clsx from 'clsx';
import { useState } from 'react';
import { Tooltip } from '../../../components/ui/tooltip/Tooltip';
import IconEmail from '../../../icons/misc/email.svg';
import IconLink from '../../../icons/misc/link.svg';
import IconFacebook from '../../../icons/social/facebook.svg';
import IconPinterest from '../../../icons/social/pinterest.svg';
import IconTwitter from '../../../icons/social/twitter.svg';

import styles from './SocialShare.module.scss';

export type SocialShareProps = {
  text: string;
  image?: string;
  placement?: 'blog';
};

/**
 * Social Share Component.
 */
export const SocialShare = ({
  text,
  image,
  placement,
  isLoading,
}: SocialShareProps & { isLoading: boolean }) => {
  const [isCopiedTooltipActive, setCopiedTooltipActive] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';

  if (isLoading) {
    return (
      <section
        className={[styles.socialShare, 'is-loading'].join(' ')}
      ></section>
    );
  }

  /**
   * Copy link url to clipboard.
   * @param event - Click event.
   */
  const copyLink = (event: React.MouseEvent) => {
    event.preventDefault();
    navigator.clipboard.writeText(url);

    setCopiedTooltipActive(true);

    setTimeout(() => {
      setCopiedTooltipActive(false);
    }, 3000);
  };

  const shareTitle = (
    <p
      className={clsx(
        'text-body-2-mobile text-body-2-desktop',
        styles.socialShare__text
      )}
    >
      <Trans id="product.share">Share:</Trans>
    </p>
  );

  const pinterestButton = (
    <a
      aria-label={t({
        id: 'product.share.pinterest',
        message: 'Pinterest opens a new tab',
      })}
      className={styles.socialShare__icon}
      href={`http://pinterest.com/pin/create/button/?url=${url}&description=${text}${
        image ? '&media=' + image : ''
      }`}
      rel="noreferrer"
      target="_blank"
    >
      <IconPinterest />
    </a>
  );

  const facebookButton = (
    <a
      aria-label={t({
        id: 'product.share.facebook',
        message: 'Facebook opens a new tab',
      })}
      className={styles.socialShare__icon}
      href={`https://www.facebook.com/sharer.php?u=${url}&t=${text}`}
      rel="noreferrer"
      target="_blank"
    >
      <IconFacebook />
    </a>
  );

  const twitterButton = (
    <a
      aria-label={t({
        id: 'product.share.twitter',
        message: 'Twitter opens a new tab',
      })}
      className={styles.socialShare__icon}
      href={`http://twitter.com/share?text=${text}&url=${url}`}
      rel="noreferrer"
      target="_blank"
    >
      <IconTwitter />
    </a>
  );

  const copyButton = (
    <a
      aria-label={t({
        id: 'product.share.copy',
        message: 'Copy link opens a new tab',
      })}
      className={styles.socialShare__icon}
      href={url}
      onClick={copyLink}
      rel="noreferrer"
      target="_blank"
    >
      <IconLink />
      <Tooltip active={isCopiedTooltipActive}>
        <p
          className={clsx(
            styles.socialShare__copiedLabel,
            'text-body-caption-desktop',
            'text-blog-body-caption-mobile'
          )}
        >
          <Trans id="product.share.copied">Link Copied</Trans>
        </p>
      </Tooltip>
    </a>
  );

  const emailButton = (
    <a
      aria-label={t({
        id: 'product.share.email',
        message: 'Email opens a new tab',
      })}
      className={styles.socialShare__icon}
      href={`mailto:?subject=${text}&body=${url}`}
    >
      <IconEmail />
    </a>
  );

  return (
    <section
      className={clsx(
        styles.socialShare,
        placement === 'blog' && styles['socialShare--blog']
      )}
    >
      {placement === 'blog' ? (
        <div
          className={clsx(
            styles.socialShare__icons,
            styles['socialShare__icons--blog']
          )}
        >
          {facebookButton} {twitterButton} {emailButton}
          {copyButton}
        </div>
      ) : (
        <div className={clsx(styles.socialShare__icons)}>
          {shareTitle} {pinterestButton} {facebookButton} {twitterButton}
          {copyButton} {emailButton}
        </div>
      )}
    </section>
  );
};
